import React from 'react';

import PropTypes from 'prop-types';
import useMetrics from '../../../hooks/useMetrics.js';

import RIQDropdownTitle from '../../../components/RIQDropdownTitle.jsx';

import RIQMultiSelectDropdown from '../../../components/RIQMultiSelectDropdown.jsx';


function deserializeOptions(selectedOptions, enums) {
    if (_.isString(selectedOptions)) {
        selectedOptions = selectedOptions.split(',');
    }

    // Interpret no selected options to mean that all are selected.
    if (_.isEmpty(selectedOptions) || _.every(selectedOptions, _.isEmpty)) {
        selectedOptions = _.keys(enums);
    }

    return selectedOptions;
}


function DropdownTitle({label, enums, currentValue, className}) {

    let selectionSummary;
    const selectedOptions = deserializeOptions(currentValue, enums);

    if (_.size(selectedOptions) === _.size(enums)) {
        selectionSummary = 'All';
    } else {
        // This is adopted from `getSelectionSummary` in postTagsFilter.js
        const MAX_SUMMARY_LENGTH_CHARS = 25;
        const selectedOptionNames = _.map(selectedOptions, selectedOption => enums[selectedOption]);
        let summaryParts = [selectedOptionNames[0]];

        const makeSummaryString = (selected) => {

            const parts = _.clone(selected);

            const numOthers = selectedOptionNames.length - selected.length;
            if (numOthers) {
                parts.push(`${numOthers} ${RivalIQ.formatter.numericLabel('other', 'others', numOthers)}`);
            }

            const conjunction = 'OR';
            if (parts.length <= 2) {
                return parts.join(` ${conjunction} `);
            } else {
                return `${_.take(parts, parts.length - 1).join(', ')}, ${conjunction} ${_.last(parts)}`;
            }
        };

        const remainingSelectedOptions = _.tail(selectedOptionNames);
        while (remainingSelectedOptions.length > 0) {
            const newSummaryParts = summaryParts.concat([remainingSelectedOptions.shift()]);
            const currentString = makeSummaryString(newSummaryParts);
            if (currentString.length > MAX_SUMMARY_LENGTH_CHARS) {
                // adding an option to the summary makes it too long, bail without adding it in.
                break;
            } else {
                summaryParts = newSummaryParts;
            }
        }

        selectionSummary = makeSummaryString(summaryParts);
    }
    className ??= 'tlw-text-sm tlw-leading-14';

    return (
        <RIQDropdownTitle
            label={label}
            className={className}
            selectionSummary={selectionSummary}
        />
    );
}

function prepareOptions(selectedOptions, enums, onlyEnableOptions, countMetricRequest, countMetricResult, infoEnums, shouldAlphabetize = false) {

    const countsById = countMetricResult ? _.fromPairs(countMetricResult.data) : undefined;
    const countFormatter = countMetricRequest && RivalIQ.metrics.new.MetricFormatter.getFormatter(countMetricRequest, countMetricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated);

    selectedOptions = deserializeOptions(selectedOptions, enums);
    const selectedIds = _.keyBy(selectedOptions);

    if (onlyEnableOptions) {
        onlyEnableOptions = _.keyBy(onlyEnableOptions);
    }

    // returns an array of objects for RIQMultiSelectDropdown
    // use "id" for metrics request and "name" for user facing name
    const options = _.map(enums, (name, id) => {
        let context;
        if (countsById) {
            context = ` (${countFormatter(countsById[id] || 0)})`;
        }
        return {
            id,
            name,
            selected: selectedIds[id],
            context: context,
            disabled: onlyEnableOptions && !onlyEnableOptions[id],
            info: infoEnums?.[id]
        };
    });

    if (shouldAlphabetize) {
        return _.sortBy(options, 'name');
    }

    return options;
}

ListeningMultiSelect.propTypes = {
    label: PropTypes.string,
    allEnumValues: PropTypes.object.isRequired,
    infoEnums: PropTypes.object,
    onlyEnableOptions: PropTypes.array,
    currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    itemFilterFcn: PropTypes.func,
    onItemSelectionsCommited: PropTypes.func.isRequired,
    fullWidthDropdown: PropTypes.bool,
    countMetricRequest: PropTypes.object,
    otherItems: PropTypes.node,
    noteText: PropTypes.string,
    tooltipText: PropTypes.string
};

export default function ListeningMultiSelect({allEnumValues, countMetricRequest, currentValue, fullWidthDropdown, infoEnums, itemFilterFcn, label, onlyEnableOptions, onItemSelectionsCommited, otherItems, noteText, shouldAlphabetize = false, tooltipText = null, trackingId}) {

    const metricRequests = {};

    if (countMetricRequest) {
        metricRequests.counts = countMetricRequest;
    }
    const {data: metricResults} = useMetrics(metricRequests);

    const dropdownOptions = prepareOptions(currentValue, allEnumValues, onlyEnableOptions, countMetricRequest, metricResults?.counts, infoEnums, shouldAlphabetize);

    const dropdownItemsClassName = fullWidthDropdown ? `tlw-w-full` : '';
    const labelClass = fullWidthDropdown ? 'tlw-text-gray-900 tlw-leading-20 tlw-text-14' : null;

    return (
        <RIQMultiSelectDropdown
            title={
                <DropdownTitle
                    label={label}
                    enums={allEnumValues}
                    currentValue={currentValue}
                    className={labelClass}
                />
            }
            dropdownOptions={dropdownOptions}
            dropdownItemsClassName={dropdownItemsClassName}
            showAllOption={true}
            onItemSelectionsCommited={onItemSelectionsCommited}
            itemFilterFcn={itemFilterFcn}
            itemFilterEnabled={Boolean(itemFilterFcn)}
            otherItems={otherItems}
            noteText={noteText}
            tooltipText={tooltipText}
            trackingId={trackingId}
        />
    );
}
